import { GridView, Search, ViewList } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { ConfirmationDialog } from "../App/Components/ConfirmationDialog/ConfirmationDialog";
import { ErrorMessage } from "../App/Components/Forms/formHelpers";
import { PageContainer } from "../App/Components/PageContainer/PageContainer";
import { PageTitle } from "../App/Components/PageTitle/PageTitle";
import { PaginationState } from "../App/Components/Table/State/PaginationState";
import { ViewArchivedGroups } from "./Components/ArchivedGroups/ViewArchivedGroups";
import { CreateGroup } from "./Components/CreateGroup/CreateGroup";
import { CardSkeleton, DisplayGroupList } from "./Components/DisplayGroupList/DisplayGroupList";
import GroupListingTable from "./Components/GroupListingTable/GroupListingTable";
import { useCandidateGroupState } from "./State/CandidateGroupsState";

export function CandidateGroups() {
  const {
    getters: {
      archivingGroupId,
      groupsPagination,
      isConfirmArchiveOpen,
      isCreatingGroup,
      isCreatingGroupError,
      isArchiving,
      isLoading,
      paginationState,
      searchInputError,
      view,
    },
    handlers: {
      handleChangeSortBy,
      handleChangeSortDirection,
      handleConfirmArchiveCancel,
      handleConfirmArchiveClose,
      handleArchiveGroup,
      handleSearchFilter,
      handleToggleCreateGroup,
      handleChangeView,
    },
    setters: { setIsCreatingGroup, setIsCreatingGroupError },
  } = useCandidateGroupState();

  const [isArchivedGroupsOpen, setIsArchivedGroupsOpen] = useState(false);

  return (
    <PageContainer>
      <Paper sx={{ mt: 4, p: { xs: 2, lg: 4 } }}>
        <Grid container>
          <Grid item xs={12} sx={{ marginBottom: "20px" }}>
            <PageTitle sx={{ lineHeight: 1 }}>Manage Groups</PageTitle>
          </Grid>
          <Grid container item xs={12} sx={{ marginBottom: "20px" }}>
            <Grid container item xs={12} md={9} lg={6} sx={{ paddingRight: { xs: 0, md: 1, lg: 0 } }}>
              <FilterAndSortGroups
                handleSearchFilter={handleSearchFilter}
                searchInputError={searchInputError}
                handleChangeSortBy={handleChangeSortBy}
                handleChangeSortDirection={handleChangeSortDirection}
                paginationState={paginationState}
                sortBy={paginationState.getters.sortBy}
                view={view}
              />
            </Grid>
            <Grid item xs={0} lg={3} xl={4} sx={{ display: { xs: "none", lg: "block" } }}>
              &nbsp;
            </Grid>
            <Grid item xs={12} sm={4} md={2} lg={2}>
              <Stack direction="row" spacing={1} justifyContent="flex-end" height={40}>
                <CreateGroup
                  isCreatingGroup={isCreatingGroup}
                  isCreatingGroupError={isCreatingGroupError}
                  setIsCreatingGroup={setIsCreatingGroup}
                  setIsCreatingGroupError={setIsCreatingGroupError}
                  handleToggleCreateGroup={handleToggleCreateGroup}
                />
                <Button
                  variant="outlined"
                  color="info"
                  sx={{ minWidth: 0 }}
                  onClick={() => handleChangeView(view === "card" ? "list" : "card")}
                  data-test="group-view-btn"
                  title={view === "card" ? "Table View" : "Grid View"}
                >
                  {view === "card" ? <ViewList /> : <GridView />}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ minHeight: "500px" }}>
          {view === "card" ? (
            <>
              {isLoading ? (
                <Box
                  sx={{
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "100%",
                      sm: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)",
                      xl: "repeat(4, 1fr)",
                    },
                    gridTemplateRows: "auto",
                    gap: 2,
                  }}
                >
                  <CardSkeleton keyName="skeleton-1" />
                  <CardSkeleton keyName="skeleton-2" />
                  <CardSkeleton keyName="skeleton-3" />
                  <CardSkeleton keyName="skeleton-4" />
                  <CardSkeleton keyName="skeleton-5" />
                  <CardSkeleton keyName="skeleton-6" />
                </Box>
              ) : groupsPagination && groupsPagination.items.length > 0 ? (
                <DisplayGroupList groups={groupsPagination.items} handleArchiveGroup={handleArchiveGroup} />
              ) : (
                <Typography component="h2" sx={{ fontSize: "1em", padding: 1 }}>
                  No groups to display.
                </Typography>
              )}
              <Grid
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <ViewArchivedGroups
                  isArchivedGroupsOpen={isArchivedGroupsOpen}
                  setIsArchivedGroupsOpen={setIsArchivedGroupsOpen}
                />
                <TablePagination
                  rowsPerPageOptions={[10, 15, 25]}
                  component="div"
                  count={groupsPagination?.totalItems ?? -1}
                  rowsPerPage={paginationState.getters.pageSize}
                  page={paginationState.getters.page}
                  onPageChange={paginationState.handlers.handleChangePage}
                  onRowsPerPageChange={paginationState.handlers.handleChangePageSize}
                  data-test="groupsPagination"
                  labelRowsPerPage="Groups per page"
                />
              </Grid>
            </>
          ) : (
            <GroupListingTable
              pagination={{
                rowsPerPage: [10, 15, 25],
                rows: paginationState.getters.pageSize,
                currentPage: paginationState.getters.page,
                handleChangePage: paginationState.handlers.handleChangePage,
                handleChangeRowsPerPage: paginationState.handlers.handleChangePageSize,
                enabled: !isLoading,
                totalCount: groupsPagination?.totalItems ?? -1,
              }}
              groups={groupsPagination?.items ?? []}
              isLoading={isLoading}
              onGroupArchive={handleArchiveGroup}
              sortBy={paginationState.getters.sortBy}
              sort={paginationState.getters.sortDirection}
              onSort={paginationState.handlers.handleRequestSort}
            />
          )}
        </Grid>
      </Paper>
      <ConfirmationDialog
        cancelMessage="Cancel"
        confirmMessage={!isArchiving ? "Archive Group" : "Archiving..."}
        handleCancel={handleConfirmArchiveCancel}
        handleClose={handleConfirmArchiveCancel}
        handleConfirm={handleConfirmArchiveClose}
        isOpen={isConfirmArchiveOpen}
        message={
          <>
            <Typography component="p" variant="body1" sx={{ fontWeight: "bold" }}>
              Are you sure you want to archive the group{" "}
              {groupsPagination?.items.find((group) => group.id === archivingGroupId)?.name ?? "N/A"}?
            </Typography>
            <Typography component="p">
              You will no longer be able to add candidates to this group, all scheduled messages will be
              cancelled and the group will be removed from the group list.
            </Typography>
            <Typography component="p" sx={{ mt: 2 }}>
              Unarchiving this group will make it available for use again.
            </Typography>
          </>
        }
        title={"Confirm Archive"}
        buttonsDisabled={isArchiving}
      />
    </PageContainer>
  );
}

const FilterAndSortGroups = ({
  handleSearchFilter,
  searchInputError,
  handleChangeSortBy,
  handleChangeSortDirection,
  paginationState,
  sortBy,
  view,
}: {
  handleSearchFilter: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchInputError: string;
  handleChangeSortBy: (sortBy: string) => void;
  handleChangeSortDirection: (sortDirection: "asc" | "desc") => void;
  paginationState: PaginationState;
  sortBy: string;
  view: "card" | "list";
}) => {
  return (
    <>
      <Grid item xs={12} md={6} sx={{ paddingBottom: { xs: 2, md: 0 } }}>
        <TextField
          label="Filter Groups"
          defaultValue={paginationState.getters.search}
          onChange={handleSearchFilter}
          type="search"
          fullWidth
          error={searchInputError.length > 0}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          data-test="groups-searchbar"
        />
        {searchInputError && <ErrorMessage message={searchInputError} />}
      </Grid>
      {view === "card" && (
        <>
          <Grid item xs={12} md={3} sx={{ paddingLeft: { xs: 0, md: 1 }, paddingBottom: { xs: 2, md: 0 } }}>
            <TextField
              label="Sort By"
              onChange={(e) => handleChangeSortBy(e.target.value)}
              fullWidth
              data-test="groups-sortby"
              select
              value={sortBy}
            >
              <MenuItem value="name" data-test="groups-sortby-name">
                Name
              </MenuItem>
              <MenuItem value="lastUpdateDate" data-test="groups-sortby-date">
                Last Updated Date
              </MenuItem>
              <MenuItem value="createdDate" data-test="groups-sortby-createddate">
                Created Date
              </MenuItem>
              <MenuItem value="type" data-test="groups-sortby-type">
                Group Type
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3} sx={{ paddingLeft: { xs: 0, md: 1 } }}>
            <TextField
              label="Sort Direction"
              onChange={(e) => handleChangeSortDirection(e.target.value as "asc" | "desc")}
              fullWidth
              data-test="groups-sort-direction"
              select
              value={paginationState.getters.sortDirection}
            >
              <MenuItem value="asc" data-test="groups-sort-asc">
                Ascending
              </MenuItem>
              <MenuItem value="desc" data-test="groups-sort-desc">
                Descending
              </MenuItem>
            </TextField>
          </Grid>
        </>
      )}
    </>
  );
};
